import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { DataTable } from '@frontend/components/enhanced/data-table'
import { RawPersonData, RawTeamPersonData } from '@frontend/core'
import { IMAGE_PRESETS } from '@frontend/core/lib/modules/images'
import { ColumnDef } from '@tanstack/react-table'
import { StaticAssetImage } from '../../modules/images/components'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/form'
import { Input } from '@/components/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/toggle-group'
import {
  useCreatePersonMutation,
  useUpdatePersonMutation
} from '@frontend/core/lib/domains/people'
import { usePeopleData, useTeamsData } from '@frontend/react'
import { SearchableDropdown, toast } from '@frontend/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

export function PersonTable({
  filteredData,
  isLoading,
  filters,
  updateFilterValue,
  showFilters
}: ReturnType<typeof usePeopleData> & {
  onTeamClick?: (teamId: string) => void
  showFilters?: (keyof ReturnType<typeof usePeopleData>['filters'])[]
}) {
  const navigate = useNavigate()

  const columns: ColumnDef<RawPersonData>[] = [
    {
      id: 'img',
      header: '',
      cell: ({ row }) => {
        const person = row.original
        return (
          <div className='flex items-center justify-center gap-2'>
            <StaticAssetImage
              src={person.img}
              alt={person.lastName}
              preset={IMAGE_PRESETS.playerThumbnail}
              className='h-8 w-8 rounded-full object-cover object-top'
            />
          </div>
        )
      },
      enableSorting: false
    },
    {
      id: 'name',
      accessorFn: (row) => row.firstName + ' ' + row.lastName,
      header: 'Name',
      cell: ({ row }) => {
        const person = row.original
        return (
          <div className='flex items-center gap-2'>
            <Button
              variant='link'
              className='text-foreground w-fit px-0 text-left'
              onClick={() => navigate(`/people/${row.original.id}`)}
            >
              {person.firstName} {person.lastName}
            </Button>
          </div>
        )
      },
      enableSorting: true,
      enableHiding: true
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <div className='w-32'>
          <Badge variant='outline' className='text-muted-foreground px-1.5'>
            {row.original.type}
          </Badge>
        </div>
      )
    },
    {
      id: 'defaultPosition',
      header: 'Position',
      cell: ({ row }) => (
        <div className='w-32'>
          <Badge variant='outline' className='text-muted-foreground px-1.5'>
            {row.original.defaultPosition}
          </Badge>
        </div>
      )
    }
  ]

  return (
    <DataTable
      name='Person'
      columns={columns}
      data={filteredData}
      dataForm={PersonForm}
      isLoading={isLoading}
      filters={filters}
      updateFilterValue={updateFilterValue}
      showFilters={showFilters ?? []}
      defaultSorting={[{ id: 'name', desc: false }]}
    />
  )
}

// Create person form //
const baseSchema = z.object({
  id: z.string().optional(),
  firstName: z.string().trim().min(1, { message: 'Required' }),
  lastName: z.string().trim().min(1, { message: 'Required' }),
  defaultPosition: z.string().optional(),
  team: z.string().optional(),
  type: z.enum(['Player', 'Official'])
})

const formSchema = baseSchema.superRefine((data, ctx) => {
  const isEmpty = (val?: string) => !val || val.trim() === ''

  // Only require a team to be selected when creating a person (i.e. when id is not provided)
  if (isEmpty(data.id) && isEmpty(data.team)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Required',
      path: ['team']
    })
  }
})

export function PersonForm({
  onSubmit,
  initialValues
}: {
  onSubmit: (values: z.infer<typeof formSchema>) => void
  initialValues?: z.infer<typeof formSchema>
}) {
  const [updatePerson, updateResult] = useUpdatePersonMutation()
  const [createPerson, createResult] = useCreatePersonMutation()
  const { options: teams } = useTeamsData()
  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      if (values.id) {
        toast.promise(
          updatePerson({
            id: values.id,
            firstName: values.firstName,
            lastName: values.lastName,
            defaultPosition: values.defaultPosition,
            type: values.type
          }).unwrap(),
          {
            loading: 'Updating...',
            success: (data: RawPersonData) => {
              onSubmit(values)
              return `${data.firstName} ${data.lastName} has been updated`
            },
            error: (error) => {
              return 'Error updating person: ' + error.message
            }
          }
        )
      } else {
        toast.promise(
          createPerson({
            teamId: values.team,
            person: {
              firstName: values.firstName,
              lastName: values.lastName,
              defaultPosition: values.defaultPosition ?? '',
              type: values.type
            }
          }).unwrap(),
          {
            loading: 'Creating...',
            success: (data: RawTeamPersonData) => {
              onSubmit(values)
              return `${data.person.firstName} ${data.person.lastName} has been created`
            },
            error: (error) => {
              return 'Error creating person: ' + error.message
            }
          }
        )
      }
    } catch (err) {
      console.error('Update failed:', err)
    }
  }

  const handleSelectTeam = (teamId: string) => {
    form.setValue('team', teamId)
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: 'Player'
    },
    values: initialValues
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-8'>
        {form.getValues('id') && (
          <div className='flex gap-4'>
            <p className='text-sm'>Person ID</p>
            <Badge variant={'secondary'}>{form.getValues('id')}</Badge>
          </div>
        )}
        <FormField
          control={form.control}
          name='firstName'
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder='' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='lastName'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder='' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='defaultPosition'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Default Position</FormLabel>
              <FormControl>
                <Input placeholder='' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!form.getValues('id') && (
          <FormField
            control={form.control}
            name='team'
            render={() => (
              <FormItem>
                <FormLabel>Team</FormLabel>
                <FormControl>
                  <SearchableDropdown
                    name='team'
                    options={teams}
                    value={form.getValues('team')}
                    onChange={(value) => handleSelectTeam(value.toString())}
                    placeholder='Select a team to link to this player'
                    searchPlaceholder='Search by team name...'
                    initialDisplayCount={20}
                    enablePagination
                    className='w-full'
                    keepConsistentWidth
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name='type'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type</FormLabel>
              <FormControl>
                <ToggleGroup
                  type='single'
                  {...field}
                  onValueChange={(val) => field.onChange(val)}
                >
                  {baseSchema.shape.type.options.map((option) => (
                    <ToggleGroupItem
                      key={option}
                      value={option}
                      className='data-[state=on]:bg-primary data-[state=on]:text-primary-foreground min-w-auto border'
                    >
                      {option}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className='flex justify-end'>
          <Button className='' type='submit'>
            {form.getValues().id
              ? updateResult.isLoading
                ? 'Updating...'
                : 'Update'
              : createResult.isLoading
                ? 'Creating...'
                : 'Create'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
